import type { AxiosInstance } from 'axios';

import type { ListResponse, users } from '@terra/contracts';

export function userEndpoints(http: AxiosInstance) {
  return {
    me: () => http.get<users.UserProfile>('/me').then((r) => r.data),

    updateProfile: (body: users.UpdateProfileRequest) =>
      http.patch<users.UserProfile>('/me', body).then((r) => r.data),

    /**
     * Close the signed-in account.
     *
     * The server empties it of anything identifying and disables it, keeping
     * the orders and tax invoices attached to it — those have a statutory
     * retention period and are the platform's books, not only the user's data.
     * Every session is revoked, so the caller is signed out everywhere.
     */
    deleteProfile: () =>
      http.delete<{ deleted: boolean; alreadyClosed: boolean }>('/me').then((r) => r.data),

    listAddresses: () => http.get<ListResponse<users.Address>>('/me/addresses').then((r) => r.data),

    addAddress: (body: users.AddressInput) =>
      http.post<users.Address>('/me/addresses', body).then((r) => r.data),

    updateAddress: (id: string, body: Partial<users.AddressInput>) =>
      http.patch<users.Address>(`/me/addresses/${id}`, body).then((r) => r.data),

    deleteAddress: (id: string) => http.delete<void>(`/me/addresses/${id}`).then(() => undefined),

    submitKyc: (body: users.KycSubmitRequest) =>
      http.post<{ status: 'SUBMITTED' }>('/me/kyc', body).then((r) => r.data),
    getKycStatus: () =>
      http
        .get<{
          status: 'NOT_SUBMITTED' | 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED';
          submittedAt?: string;
          reviewedAt?: string;
          reviewNote?: string;
          legalName?: string | null;
          pan?: string | null;
          aadhaar?: string | null;
          gstin?: string | null;
          bankAccountName?: string | null;
          bankAccountNumber?: string | null;
          bankIfsc?: string | null;
          panDocFileId?: string | null;
          addressProofFileId?: string | null;
          gstDocFileId?: string | null;
          upiId?: string | null;
          payoutPreference?: string | null;
          panDocUrl?: string | null;
          addressProofDocUrl?: string | null;
        }>('/me/kyc')
        .then((r) => r.data),

    adminGetUserKyc: (userId: string) =>
      http
        .get<{
          status: 'NOT_SUBMITTED' | 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED';
          submittedAt?: string;
          reviewedAt?: string;
          reviewNote?: string;
          legalName?: string | null;
          pan?: string | null;
          aadhaar?: string | null;
          gstin?: string | null;
          bankAccountName?: string | null;
          bankAccountNumber?: string | null;
          bankIfsc?: string | null;
          panDocFileId?: string | null;
          addressProofFileId?: string | null;
          gstDocFileId?: string | null;
          upiId?: string | null;
          payoutPreference?: string | null;
          panDocUrl?: string | null;
          addressProofDocUrl?: string | null;
        }>(`/users/${userId}/kyc`)
        .then((r) => r.data),

    adminReviewUserKyc: (
      userId: string,
      body: { status: 'APPROVED' | 'REJECTED'; reviewNote?: string },
    ) =>
      http
        .post<{ status: 'APPROVED' | 'REJECTED' }>(`/users/${userId}/kyc/review`, body)
        .then((r) => r.data),

    // admin
    listUsers: (params?: {
      q?: string;
      status?: string;
      role?: string;
      page?: number;
      pageSize?: number;
    }) => http.get<ListResponse<users.UserProfile>>('/users', { params }).then((r) => r.data),
    createUser: (body: users.CreateUserRequest) =>
      http.post<users.UserProfile>('/users', body).then((r) => r.data),
    updateUser: (userId: string, body: users.UpdateUserRequest) =>
      http.put<users.UserProfile>(`/users/${userId}`, body).then((r) => r.data),
    setRoles: (userId: string, roles: string[]) =>
      http
        .put<{ id: string; roles: string[] }>(`/users/${userId}/roles`, { roles })
        .then((r) => r.data),
    setStatus: (userId: string, status: string) =>
      http
        .patch<{ id: string; status: string }>(`/users/${userId}/status`, { status })
        .then((r) => r.data),
  };
}

export type UserEndpoints = ReturnType<typeof userEndpoints>;
